import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Input,
  Label,
} from '@features/ui';
import { useAuthStore } from '@stores/authStore';
import { SUPPORT_EMAIL } from '@src/constants';

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Email is invalid').required('Email is required'),
  phoneNumber: yup.string().required('Phone number is required'),
  street: yup.string().required('Street is required'),
  country: yup.string().required('Country is required'),
  state: yup.string().required('State is required'),
  city: yup.string().required('City is required'),
  zipCode: yup.string().required('Zip code is required'),
  xHandle: yup.string(),
  fbHandle: yup.string(),
  igHandle: yup.string(),
  discordHandle: yup.string(),
});

export type UserProfileFormData = yup.InferType<typeof schema>;

export const UserProfileForm = () => {
  const { t } = useTranslation();
  const { auth } = useAuthStore();

  const { control, reset } = useForm<UserProfileFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      country: '',
      state: '',
      city: '',
      zipCode: '',
    },
  });

  useEffect(() => {
    if (auth.user) {
      const {
        first_name,
        last_name,
        email,
        phone_number,
        country,
        state,
        city,
        zipcode,
      } = auth.user;

      const user = {
        firstName: first_name,
        lastName: last_name,
        email,
        phoneNumber: phone_number,
        country: country,
        state,
        city,
        zipCode: zipcode,
      };

      reset(user);
    }
  }, [auth.user, reset]);

  const handleCopyAndEmail = async () => {
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  };

  return (
    <form autoComplete="off">
      <h2 className="text-[23px] medium mb-5">Profile</h2>
      <div className="bg-card rounded-xl p-6 mb-7.5">
        <div className="gap-5 space-y-5">
          {/* Avatar */}
          <div className="flex items-center gap-[15px] pb-2.5">
            <div className="relative">
              <Avatar className="w-[153px] h-[153px]">
                <AvatarImage
                  alt="Profile picture"
                  src={auth.user?.avatar ?? ''}
                />
                <AvatarFallback>UN</AvatarFallback>
              </Avatar>
              {/* <div className="absolute -bottom-1 -right-1 bg-red-500 p-1 rounded-full">
                <Camera className="h-4 w-4 text-white" />
              </div> */}
            </div>

            <div>
              <h2 className="text-2xl font-medium mb-2.5">
                {auth.user?.first_name ?? ''} {auth.user?.last_name ?? ''}
              </h2>
              <p className="font-medium">
                {auth.user?.email ?? 'test@gmail.com'}
              </p>
            </div>
          </div>

          {/* User info */}
          <div className="flex flex-col lg:flex-row gap-5">
            <Controller
              control={control}
              name="firstName"
              render={({ field }) => (
                <div className="space-y-2.5 w-full lg:w-1/4">
                  <Label htmlFor="firstName">
                    {t('common.form.firstName')}
                  </Label>
                  <Input
                    disabled
                    className={'rounded-[10px] border-[#979797]'}
                    id="firstName"
                    placeholder={'First Name'}
                    {...field}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="lastName"
              render={({ field }) => (
                <div className="space-y-2.5 w-full lg:w-1/4">
                  <Label htmlFor="lastName">{t('common.form.lastName')}</Label>
                  <Input
                    disabled
                    className={'rounded-[10px] border-[#979797]'}
                    id="lastName"
                    placeholder={'Last Name'}
                    {...field}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <div className="space-y-2.5 w-full lg:[width:calc(50%_+_20px)]">
                  <Label htmlFor="email">{t('common.form.email')}</Label>
                  <Input
                    disabled
                    className={'rounded-[10px] border-[#979797]'}
                    id="email"
                    type="email"
                    {...field}
                  />
                </div>
              )}
            />
          </div>

          {/* Phone + Country */}
          <div className="flex flex-col lg:flex-row gap-5">
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <div className="space-y-2.5 w-full">
                  <Label htmlFor="phoneNumber">
                    {t('common.form.phoneNumber')}
                  </Label>
                  <Input
                    disabled
                    className={'rounded-[10px] border-[#979797]'}
                    id="phoneNumber"
                    {...field}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="country"
              render={({ field }) => (
                <div className="space-y-2.5 w-full">
                  <Label htmlFor="country">{t('common.form.country')}</Label>
                  <Input
                    disabled
                    className={'rounded-[10px] border-[#979797]'}
                    id="country"
                    {...field}
                  />
                </div>
              )}
            />
          </div>

          {/* Address */}
          <div className="flex flex-col lg:flex-row gap-5">
            <Controller
              control={control}
              name="state"
              render={({ field }) => (
                <div className="space-y-2.5 w-full">
                  <Label htmlFor="state">
                    {t('common.form.state')}
                    <span className={'text-[#DF0000] ml-0.5'}>*</span>
                  </Label>
                  <Input
                    disabled
                    className={'rounded-[10px] border-[#979797]'}
                    id="state"
                    {...field}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="street"
              render={({ field }) => (
                <div className="space-y-2.5 w-full">
                  <Label htmlFor="street">{t('common.form.street')}</Label>
                  <Input
                    disabled
                    className={'rounded-[10px] border-[#979797]'}
                    id="street"
                    {...field}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="city"
              render={({ field }) => (
                <div className="space-y-2.5 w-full">
                  <Label htmlFor="city">{t('common.form.city')}</Label>
                  <Input
                    disabled
                    className={'rounded-[10px] border-[#979797]'}
                    id="city"
                    {...field}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="zipCode"
              render={({ field }) => (
                <div className="space-y-2.5 w-full">
                  <Label htmlFor="zipCode">{t('common.form.zipCode')}</Label>
                  <Input
                    disabled
                    className={'rounded-[10px] border-[#979797]'}
                    id="zipCode"
                    {...field}
                  />
                </div>
              )}
            />
          </div>

          <div className="md:col-span-2 mt-4">
            <Button
              className="w-full lg:w-auto text-white"
              size={'lg'}
              onClick={handleCopyAndEmail}
            >
              {t('common.form.contactSpForChanges')}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
